import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../components/ui/sheet';
import {
  Inbox as InboxIcon, Search, Phone, Building2, MapPin, Calendar,
  User, FileText, UserPlus, Trash2, CheckCircle2, Mail as MailIcon,
  Briefcase, Globe, Eye, Clock, Users, MousePointerClick, Code2, Wallet, MoreHorizontal,
} from 'lucide-react';
import { toast } from 'sonner';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';

const API = process.env.REACT_APP_BACKEND_URL;
const PUBLIC_DEVIS_ENDPOINT = `${API}/api/public/devis-request`;
const PUBLIC_VISIT_ENDPOINT = `${API}/api/public/site-visit`;

const STATUT_STYLES = {
  'Nouvelle':       { bg: 'bg-blue-100',    text: 'text-blue-700',    border: 'border-l-blue-500',    dot: 'bg-blue-500' },
  'Traitée':        { bg: 'bg-slate-100',   text: 'text-slate-600',   border: 'border-l-slate-400',   dot: 'bg-slate-400' },
  'Prospect_créé':  { bg: 'bg-purple-100',  text: 'text-purple-700',  border: 'border-l-purple-500',  dot: 'bg-purple-500' },
  'Devis_créé':     { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-l-emerald-500', dot: 'bg-emerald-500' },
};

const initialsFromName = (name = '') =>
  name.split(' ').filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('') || '•';

const flagEmoji = (code) => {
  if (!code || code.length !== 2) return '';
  const base = 0x1F1E6;
  const c = code.toUpperCase();
  return String.fromCodePoint(base + c.charCodeAt(0) - 65) + String.fromCodePoint(base + c.charCodeAt(1) - 65);
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' });
  } catch { return iso; }
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `il y a ${Math.floor(diff)}s`;
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  return `il y a ${Math.floor(diff / 86400)} j`;
};

export default function Inbox({ user, onLogout }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState('devis'); // 'devis' | 'visites'

  // ─────── Demandes de devis ───────
  const [requests, setRequests] = useState([]);
  const [loadingReq, setLoadingReq] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sortOrder, setSortOrder] = useState('recent');
  const [visibleCount, setVisibleCount] = useState(PAGE);
  const [showSnippet, setShowSnippet] = useState(false);
  const [snippetKind, setSnippetKind] = useState('devis'); // 'devis' | 'visite'

  // ─────── Visites ───────
  const [visits, setVisits] = useState([]);
  const [visitStats, setVisitStats] = useState({ total: 0, today: 0, last_7_days: 0, unique_visitors: 0 });
  const [loadingVisits, setLoadingVisits] = useState(false);
  const [visitSearch, setVisitSearch] = useState('');

  const loadRequests = async () => {
    setLoadingReq(true);
    try {
      const r = await axios.get(`${API}/api/inbox/devis-requests`);
      setRequests(r.data || []);
    } catch {
      toast.error('Erreur au chargement des demandes');
    } finally {
      setLoadingReq(false);
    }
  };

  const loadVisits = async () => {
    setLoadingVisits(true);
    try {
      const [vRes, sRes] = await Promise.all([
        axios.get(`${API}/api/inbox/site-visits`),
        axios.get(`${API}/api/inbox/site-visits/stats`),
      ]);
      setVisits(vRes.data || []);
      setVisitStats(sRes.data || {});
    } catch {
      toast.error('Erreur au chargement des visites');
    } finally {
      setLoadingVisits(false);
    }
  };

  useEffect(() => { loadRequests(); }, []);
  useEffect(() => {
    if (tab !== 'visites') return undefined;
    loadVisits();
    const t = setInterval(() => {
      Promise.all([axios.get(`${API}/api/inbox/site-visits`), axios.get(`${API}/api/inbox/site-visits/stats`)])
        .then(([v, st]) => { setVisits(v.data || []); setVisitStats(st.data || {}); })
        .catch(() => {});
    }, 20000);
    return () => clearInterval(t);
  }, [tab]);

  // ═══════════ Demandes de devis ═══════════
  const filtered = useMemo(() => {
    return (requests || []).filter(r => {
      if (filter !== 'all' && r.statut !== filter) return false;
      if (search) {
        const s = search.toLowerCase();
        return `${r.societe} ${r.nom_complet} ${r.telephone} ${r.email} ${r.secteur}`.toLowerCase().includes(s);
      }
      return true;
    });
  }, [requests, filter, search]);

  const sorted = useMemo(() => [...filtered].sort((a, b) => {
    const c = String(a.received_at || '').localeCompare(String(b.received_at || ''));
    return sortOrder === 'recent' ? -c : c;
  }), [filtered, sortOrder]);

  const stats = useMemo(() => ({
    total: requests.length,
    nouvelles: requests.filter(r => r.statut === 'Nouvelle').length,
    traitees: requests.filter(r => r.statut === 'Traitée' || r.statut === 'Devis_créé').length,
    prospects: requests.filter(r => r.statut === 'Prospect_créé' || r.statut === 'Devis_créé').length,
  }), [requests]);

  const handleAjouterProspect = async (req) => {
    try {
      const payload = {
        entreprise: req.societe,
        nom_contact: req.nom_complet,
        email: req.email || '',
        telephone: req.telephone,
        adresse: req.adresse || '',
        secteur: req.secteur || '',
        source: 'Boîte de réception',
        statut: 'Prospect',
        notes: req.message || '',
      };
      const res = await axios.post(`${API}/api/prospects`, payload);
      const prospectId = res.data?.id || res.data?.prospect_id;
      await axios.patch(`${API}/api/inbox/devis-requests/${req.id}`, {
        statut: 'Prospect_créé',
        prospect_id: prospectId,
      });
      toast.success('Prospect créé ✓');
      loadRequests();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur création prospect');
    }
  };

  const handleEffectuerDevis = async (req) => {
    try {
      let prospectId = req.prospect_id;
      if (!prospectId) {
        const listRes = await axios.get(`${API}/api/prospects`);
        const found = (listRes.data || []).find(p =>
          (p.entreprise || '').trim().toLowerCase() === req.societe.trim().toLowerCase()
        );
        if (found) prospectId = found.id;
        else {
          const createRes = await axios.post(`${API}/api/prospects`, {
            entreprise: req.societe,
            nom_contact: req.nom_complet,
            contact: req.nom_complet || '',
            email: req.email || '',
            telephone: req.telephone,
            adresse: req.adresse || '',
            secteur: req.secteur || '',
            source: 'Boîte de réception',
            statut: 'Prospect',
            notes: req.message || '',
          });
          prospectId = createRes.data?.id || createRes.data?.prospect_id;
        }
        await axios.patch(`${API}/api/inbox/devis-requests/${req.id}`, { prospect_id: prospectId });
      }
      // Stocker la demande complète pour auto-remplissage côté Prospects
      try {
        sessionStorage.setItem('inbox_autofill_payload', JSON.stringify({
          inbox_id:     req.id,
          societe:      req.societe,
          nom_complet:  req.nom_complet,
          email:        req.email,
          telephone:    req.telephone,
          adresse:      req.adresse,
          secteur:      req.secteur,
          message:      req.message,
          budget:       req.budget,
          duree:        req.duree,
          panneaux:     req.panneaux || [],
          date_debut_campagne: req.date_debut_campagne || '',
          date_fin_campagne:   req.date_fin_campagne   || '',
        }));
      } catch {}
      toast.success('Redirection vers le formulaire de devis…');
      navigate(`/prospects?inbox_id=${req.id}&prospect_id=${prospectId}&open_devis=1`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la redirection');
    }
  };

  const handleDelete = async (req) => {
    if (!window.confirm(`Supprimer la demande de ${req.societe} ?`)) return;
    try {
      await axios.delete(`${API}/api/inbox/devis-requests/${req.id}`);
      toast.success('Demande supprimée');
      loadRequests();
    } catch { toast.error('Erreur suppression'); }
  };

  const handleMarkTreated = async (req) => {
    try {
      await axios.patch(`${API}/api/inbox/devis-requests/${req.id}`, { statut: 'Traitée' });
      toast.success('Marquée comme traitée');
      loadRequests();
    } catch { toast.error('Erreur mise à jour'); }
  };

  // ═══════════ Visites ═══════════
  const filteredVisits = useMemo(() => {
    if (!visitSearch) return visits;
    const s = visitSearch.toLowerCase();
    return visits.filter(v =>
      `${v.page} ${v.ip} ${v.city} ${v.country} ${v.referer} ${v.user_agent} ${v.visitor_name || ''} ${v.region || ''}`.toLowerCase().includes(s)
    );
  }, [visits, visitSearch]);

  const handleDeleteVisit = async (v) => {
    try {
      await axios.delete(`${API}/api/inbox/site-visits/${v.id}`);
      setVisits(prev => prev.filter(x => x.id !== v.id));
      axios.get(`${API}/api/inbox/site-visits/stats`).then((r) => setVisitStats(r.data || {})).catch(() => {});
    } catch { toast.error('Erreur suppression'); }
  };

  const handleClearVisits = async () => {
    if (!window.confirm('Vider tout l\'historique des visites ? Cette action est irréversible.')) return;
    try {
      await axios.delete(`${API}/api/inbox/site-visits`);
      toast.success('Historique vidé');
      loadVisits();
    } catch { toast.error('Erreur vidage'); }
  };

  const handleSeedDemo = async () => {
    try {
      const r = await axios.post(`${API}/api/inbox/site-visits-demo`);
      toast.success(`${r.data?.inserted || 0} visites de Bangui ajoutées`);
      loadVisits();
    } catch { toast.error("Erreur d'ajout"); }
  };

  const handleClearDemo = async () => {
    if (!window.confirm('Supprimer les 50 visites de démonstration ? Les vraies visites sont conservées.')) return;
    try {
      const r = await axios.delete(`${API}/api/inbox/site-visits-demo`);
      toast.success(`${r.data?.deleted || 0} visites démo supprimées`);
      loadVisits();
    } catch { toast.error('Erreur de suppression'); }
  };

  return (
    <Layout user={user} onLogout={onLogout} currentPage="inbox">
      <div className="space-y-5" data-testid="inbox-root">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-4xl font-extrabold text-slate-900">Boîte de réception</h1>
            <p className="text-slate-500 mt-1">Demandes de devis et visites du site www.sesamrca.com</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => { setSnippetKind(tab === 'visites' ? 'visite' : 'devis'); setShowSnippet(true); }}
              className="gap-2"
              data-testid="btn-snippet"
            >
              <Code2 className="h-4 w-4" /> Code d'intégration
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 w-fit">
          <button
            onClick={() => setTab('devis')}
            className={`px-5 py-2 rounded-lg text-sm font-semibold transition flex items-center gap-2 ${
              tab === 'devis' ? 'bg-blue-600 text-white shadow' : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
            }`}
            data-testid="tab-devis"
          >
            <MailIcon className="h-4 w-4" />
            Demandes de devis
            {stats.nouvelles > 0 && (
              <Badge className="bg-white/90 text-blue-700 ml-1 h-5">{stats.nouvelles}</Badge>
            )}
          </button>
          <button
            onClick={() => setTab('visites')}
            className={`px-5 py-2 rounded-lg text-sm font-semibold transition flex items-center gap-2 ${
              tab === 'visites' ? 'bg-blue-600 text-white shadow' : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
            }`}
            data-testid="tab-visites"
          >
            <Eye className="h-4 w-4" />
            Visites
            {visitStats.today > 0 && (
              <Badge className="bg-white/90 text-blue-700 ml-1 h-5">+{visitStats.today} aujourd'hui</Badge>
            )}
          </button>
        </div>

        {/* ═══════ ONGLET DEVIS ═══════ */}
        {tab === 'devis' && (
          <div className="space-y-4 pt-1">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KpiCard label="Total demandes"   value={stats.total}     total={stats.total} tone="slate"  testid="kpi-total" />
              <KpiCard label="Nouvelles"        value={stats.nouvelles} total={stats.total} tone="blue"   testid="kpi-nouvelles" />
              <KpiCard label="Prospects créés"  value={stats.prospects} total={stats.total} tone="purple" testid="kpi-prospects" />
              <KpiCard label="Traitées"         value={stats.traitees}  total={stats.total} tone="green"  testid="kpi-traitees" />
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_420px] gap-4 items-start">
              <div className="space-y-3 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {[
                    { id: 'all', label: 'Toutes', count: stats.total },
                    { id: 'Nouvelle', label: 'Nouvelles', count: stats.nouvelles },
                    { id: 'Prospect_créé', label: 'Prospects', count: requests.filter(r => r.statut === 'Prospect_créé').length },
                    { id: 'Devis_créé', label: 'Devis créés', count: requests.filter(r => r.statut === 'Devis_créé').length },
                    { id: 'Traitée', label: 'Traitées', count: requests.filter(r => r.statut === 'Traitée').length },
                  ].map(f => (
                    <button
                      key={f.id}
                      onClick={() => { setFilter(f.id); setVisibleCount(PAGE); }}
                      className={`px-4 h-9 rounded-full text-sm font-bold transition-colors ${filter === f.id ? 'bg-blue-600 text-white' : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50'}`}
                      data-testid={`filter-${f.id}`}
                    >
                      {f.label} &nbsp;{f.count}
                    </button>
                  ))}
                  <div className="flex-1" />
                  <Input placeholder="Rechercher société, contact…" value={search} onChange={(e) => setSearch(e.target.value)} className="h-9 w-64 bg-white" data-testid="inbox-search" />
                  <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700" data-testid="inbox-sort">
                    <option value="recent">Trier : plus récentes</option>
                    <option value="old">Trier : plus anciennes</option>
                  </select>
                </div>

                {loadingReq ? (
                  <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-10 w-10 border-b-4 border-blue-600" /></div>
                ) : filtered.length === 0 ? (
                  <Card>
                    <CardContent className="p-10 text-center">
                      <InboxIcon className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                      <h3 className="text-lg font-semibold text-slate-700 mb-1">Aucune demande</h3>
                      <p className="text-sm text-slate-500">Les demandes reçues depuis sesamrca.com apparaîtront ici.</p>
                    </CardContent>
                  </Card>
                ) : (
                  <>
                    {sorted.slice(0, visibleCount).map(req => (
                      <RequestCard key={req.id} request={req}
                        onEffectuer={() => handleEffectuerDevis(req)} onProspect={() => handleAjouterProspect(req)}
                        onDelete={() => handleDelete(req)} onMarkTreated={() => handleMarkTreated(req)} />
                    ))}
                    {sorted.length > visibleCount && (
                      <button onClick={() => setVisibleCount((v) => v + PAGE)} className="w-full h-10 rounded-xl border-2 border-dashed border-blue-200 text-blue-700 font-bold hover:bg-blue-50 transition-colors" data-testid="btn-show-more">
                        Afficher les {sorted.length - visibleCount} autres demandes ›
                      </button>
                    )}
                  </>
                )}
                <IndicateursCard requests={requests} />
              </div>
              <div className="space-y-4">
                <EvolutionDemandes requests={requests} />
                <RepartitionStatut stats={stats} requests={requests} />
              </div>
            </div>
          </div>
        )}

        {/* ═══════ ONGLET VISITES ═══════ */}
        {tab === 'visites' && (
          <div className="space-y-6 pt-2">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KpiCard label="Visites totales" value={visitStats.total}            tone="sky"    testid="kpi-visites-total" />
              <KpiCard label="Aujourd'hui"      value={visitStats.today}            tone="blue"   testid="kpi-visites-today" />
              <KpiCard label="7 derniers jours" value={visitStats.last_7_days}      tone="indigo" testid="kpi-visites-7j" />
              <KpiCard label="Visiteurs uniques" value={visitStats.unique_visitors} tone="green"  testid="kpi-visites-uniques" />
            </div>

            <div className="flex items-center gap-3 flex-wrap bg-white p-3 rounded-xl border border-slate-200 shadow-sm">
              <div className="flex-1 min-w-[220px] relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Rechercher page, ville, pays, IP…"
                  value={visitSearch}
                  onChange={(e) => setVisitSearch(e.target.value)}
                  className="pl-9 h-9"
                  data-testid="visits-search"
                />
              </div>
              <Button variant="outline" size="sm" onClick={loadVisits} className="gap-2">
                <Clock className="h-4 w-4" /> Rafraîchir
              </Button>
              <Button
                variant="outline" size="sm"
                onClick={handleClearVisits}
                className="gap-2 text-red-600 border-red-200 hover:bg-red-50"
                data-testid="btn-clear-visits"
              >
                <Trash2 className="h-4 w-4" /> Vider l'historique
              </Button>
              {visits.some((v) => v.is_demo) ? (
                <Button variant="outline" size="sm" onClick={handleClearDemo}
                  className="gap-2 text-amber-700 border-amber-200 hover:bg-amber-50" data-testid="btn-clear-demo-visits">
                  <Trash2 className="h-4 w-4" /> Supprimer les visites démo
                </Button>
              ) : user?.role === 'admin' && (
                <Button variant="outline" size="sm" onClick={handleSeedDemo}
                  className="gap-2 text-blue-700 border-blue-200 hover:bg-blue-50" data-testid="btn-seed-demo-visits">
                  <Users className="h-4 w-4" /> Ajouter 50 visites démo
                </Button>
              )}
            </div>

            <Card className="overflow-hidden">
              <CardContent className="p-0">
                {loadingVisits ? (
                  <div className="flex items-center justify-center h-72">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600" />
                  </div>
                ) : filteredVisits.length === 0 ? (
                  <div className="p-12 text-center">
                    <Eye className="h-16 w-16 text-slate-300 mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-slate-700 mb-1">Aucune visite enregistrée</h3>
                    <p className="text-sm text-slate-500 mb-4">
                      Collez le code d'intégration sur sesamrca.com pour commencer à tracer les visiteurs.
                    </p>
                    <Button variant="outline" onClick={() => { setSnippetKind('visite'); setShowSnippet(true); }} className="gap-2">
                      <Code2 className="h-4 w-4" /> Voir le code d'intégration
                    </Button>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr className="text-left text-xs uppercase text-slate-500">
                          <th className="py-3 px-4 w-32">Date / heure</th>
                          <th className="py-3 px-4">Visiteur</th>
                          <th className="py-3 px-4">Localisation</th>
                          <th className="py-3 px-4">Page visitée</th>
                          <th className="py-3 px-4">Source</th>
                          <th className="py-3 px-4 w-12"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredVisits.map((v) => (
                          <VisitRow key={v.id} visit={v} onDelete={() => handleDeleteVisit(v)} />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Sheet : Snippet code */}
      <Sheet open={showSnippet} onOpenChange={setShowSnippet}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto p-6">
          <SheetHeader className="mb-4">
            <SheetTitle className="flex items-center gap-2 text-xl">
              <Code2 className="h-5 w-5 text-blue-600" />
              Intégration sesamrca.com — {snippetKind === 'visite' ? 'Visites' : 'Demandes de devis'}
            </SheetTitle>
            <SheetDescription>
              {snippetKind === 'visite'
                ? 'Collez ce code sur www.sesamrca.com pour que chaque visite remonte automatiquement dans l\'onglet Visites.'
                : 'Collez ce code pour que les demandes de devis arrivent automatiquement dans la boîte de réception.'}
            </SheetDescription>
          </SheetHeader>
          <div className="flex items-center gap-2 mb-4 border border-slate-200 rounded-lg p-1 w-fit">
            <button
              onClick={() => setSnippetKind('devis')}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold ${snippetKind === 'devis' ? 'bg-blue-600 text-white' : 'text-slate-600'}`}
            >Demandes de devis</button>
            <button
              onClick={() => setSnippetKind('visite')}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold ${snippetKind === 'visite' ? 'bg-blue-600 text-white' : 'text-slate-600'}`}
            >Visites</button>
          </div>
          {snippetKind === 'visite'
            ? <VisitSnippet endpoint={PUBLIC_VISIT_ENDPOINT} />
            : <DevisSnippet endpoint={PUBLIC_DEVIS_ENDPOINT} />
          }
        </SheetContent>
      </Sheet>
    </Layout>
  );
}

// ═══════════ COMPONENTS ═══════════

const KPI_BORDER = { slate: 'border-l-[#16213f]', blue: 'border-l-blue-600', purple: 'border-l-violet-500', green: 'border-l-green-500', sky: 'border-l-sky-500', indigo: 'border-l-indigo-500' };

const KPI_BAR = { slate: 'bg-blue-600', blue: 'bg-blue-500', purple: 'bg-violet-500', green: 'bg-green-500', sky: 'bg-sky-500', indigo: 'bg-indigo-500' };
const PAGE = 5;

function KpiCard({ label, value, total, tone = 'slate', testid }) {
  const pct = total ? Math.round(((value || 0) / total) * 100) : 0;
  return (
    <div className={`bg-white rounded-xl border border-slate-200 border-l-4 ${KPI_BORDER[tone]} px-4 py-3 shadow-sm`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600">{label}</p>
        <p className="text-2xl font-extrabold text-slate-900">{value || 0}</p>
      </div>
      {total !== undefined && (
        <div className="h-1 rounded-full bg-slate-100 mt-2 overflow-hidden"><div className={`h-full ${KPI_BAR[tone]}`} style={{ width: `${pct}%` }} /></div>
      )}
    </div>
  );
}

const MONTHS_SHORT = ['Janv', 'Févr', 'Mars', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];

function EvolutionDemandes({ requests }) {
  const now = new Date();
  const months = Array.from({ length: 6 }, (_, i) => new Date(now.getFullYear(), now.getMonth() - 5 + i, 1));
  const data = months.map((m) => ({
    label: MONTHS_SHORT[m.getMonth()],
    n: requests.filter((r) => { const d = new Date(r.received_at); return d.getFullYear() === m.getFullYear() && d.getMonth() === m.getMonth(); }).length,
  }));
  const max = Math.max(1, ...data.map((d) => d.n));
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5" data-testid="inbox-evolution">
      <h3 className="text-lg font-bold text-slate-900">Évolution des demandes</h3>
      <p className="text-sm text-slate-500">Demandes reçues par mois, de {MONTHS_SHORT[months[0].getMonth()].toLowerCase()} à {MONTHS_SHORT[now.getMonth()].toLowerCase()} {now.getFullYear()}</p>
      <div className="flex items-end gap-3 h-36 mt-4">
        {data.map((d, i) => (
          <div key={d.label} className="flex-1 flex flex-col items-center justify-end h-full">
            <span className="text-xs font-bold text-slate-800 mb-1">{d.n}</span>
            <div className={`w-full rounded-t-md transition-[height] duration-700 ${i === 5 ? 'bg-blue-600' : 'bg-blue-200'}`} style={{ height: `${Math.max(3, (d.n / max) * 100)}%` }} />
            <span className="text-xs text-slate-500 mt-1">{d.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function BarRow({ label, value, total, color, testid }) {
  const pct = total ? (value / total) * 100 : 0;
  return (
    <div data-testid={testid}>
      <div className="flex justify-between text-sm"><span className="text-slate-700">{label}</span><b className="text-slate-900">{value}</b></div>
      <div className="h-1.5 rounded-full bg-slate-100 mt-1 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} /></div>
    </div>
  );
}

function RepartitionStatut({ stats, requests }) {
  const devis = requests.filter((r) => r.statut === 'Devis_créé').length;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-3" data-testid="inbox-repartition">
      <div>
        <h3 className="text-lg font-bold text-slate-900">Répartition par statut</h3>
        <p className="text-sm text-slate-500">Sur {stats.total} demandes reçues</p>
      </div>
      <BarRow label="Traitées" value={stats.traitees} total={stats.total} color="#22c55e" testid="rep-traitees" />
      <BarRow label="Prospects créés" value={stats.prospects} total={stats.total} color="#8b5cf6" testid="rep-prospects" />
      <BarRow label="Nouvelles" value={stats.nouvelles} total={stats.total} color="#3b82f6" testid="rep-nouvelles" />
      <BarRow label="Devis créés" value={devis} total={stats.total} color="#0ea5e9" testid="rep-devis" />
    </div>
  );
}

function IndicateursCard({ requests }) {
  const t = requests.length || 1;
  const traitees = requests.filter((r) => r.statut !== 'Nouvelle').length;
  const prospects = requests.filter((r) => r.statut === 'Prospect_créé' || r.statut === 'Devis_créé').length;
  const site = requests.filter((r) => !r.source || /sesamrca/i.test(r.source)).length;
  const items = [
    ['Taux de traitement', traitees, '#22c55e', 'ind-traitement'],
    ['Conversion en prospect', prospects, '#8b5cf6', 'ind-conversion'],
    ['Demandes via sesamrca.com', site, '#2563eb', 'ind-site'],
  ];
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5" data-testid="inbox-indicateurs">
      <h3 className="text-lg font-bold text-slate-900">Indicateurs</h3>
      <p className="text-sm text-slate-500">Traitement et conversion des demandes</p>
      <div className="grid md:grid-cols-3 gap-6 mt-3">
        {items.map(([l, v, c, id]) => {
          const pct = requests.length ? Math.round((v / t) * 100) : 0;
          return (
            <div key={l} data-testid={id}>
              <div className="flex justify-between text-sm"><span className="text-slate-700">{l}</span><b className="text-slate-900">{pct} %</b></div>
              <div className="h-1.5 rounded-full bg-slate-100 mt-1.5 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${pct}%`, background: c }} /></div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const PANEL_COLORS = {
  'Déroulant':   { bg: 'bg-blue-500',   label: 'Panneaux déroulants' },
  'Deroulant':   { bg: 'bg-blue-500',   label: 'Panneaux déroulants' },
  'LED':         { bg: 'bg-purple-500', label: 'Panneaux LED' },
  'Grand Format':{ bg: 'bg-orange-500', label: 'Panneau grand format' },
  'Grand format':{ bg: 'bg-orange-500', label: 'Panneau grand format' },
  'Poteau':      { bg: 'bg-emerald-500',label: 'Panneaux poteaux' },
};
const AVATAR_PALETTE = [
  'from-blue-600 to-indigo-700',
  'from-emerald-500 to-emerald-700',
  'from-purple-500 to-purple-700',
  'from-amber-500 to-orange-600',
  'from-rose-500 to-pink-600',
  'from-cyan-500 to-sky-700',
];
const avatarGradientFor = (name = '') => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
};
const formatShortDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return iso; }
};

const TYPE_STYLE = {
  'Déroulant': ['border-t-emerald-500', 'Panneaux déroulants'], 'Deroulant': ['border-t-emerald-500', 'Panneaux déroulants'],
  'LED': ['border-t-amber-500', 'Panneaux LED'], 'Grand Format': ['border-t-violet-500', 'Panneau grand format'],
  'Grand format': ['border-t-violet-500', 'Panneau grand format'], 'Poteau': ['border-t-cyan-500', 'Panneaux poteaux'],
};
const FALLBACK_BORDERS = ['border-t-rose-500', 'border-t-sky-500', 'border-t-lime-500', 'border-t-fuchsia-500', 'border-t-orange-500'];
const STATUT_BADGE = {
  'Nouvelle': ['bg-blue-50 text-blue-700', 'Nouvelle'], 'Traitée': ['bg-green-50 text-green-700', 'Traitée'],
  'Prospect_créé': ['bg-violet-50 text-violet-700', 'Prospect créé'], 'Devis_créé': ['bg-emerald-50 text-emerald-700', 'Devis créé'],
};
const waLink = (tel = '') => {
  let d = tel.replace(/[^\d]/g, '');
  if (d.length === 8) d = `236${d}`;
  return `https://wa.me/${d}`;
};

function PanelTypeBox({ p, wide }) {
  const [border, label] = TYPE_STYLE[p.type_panneau] || [FALLBACK_BORDERS[(p.type_panneau || '').length % FALLBACK_BORDERS.length], p.type_panneau || 'Panneau'];
  const emps = p.emplacements || [];
  const nb = Number(p.nb_panneaux) || emps.length;
  const aff = emps.reduce((x, e) => x + (Number(e.affiches) || 0), 0);
  const missing = nb - emps.length;
  return (
    <div className={`rounded-xl border border-slate-200 border-t-4 ${border} p-4 bg-white`} data-testid={`panel-box-${(p.type_panneau || 'autre').replace(/\s+/g, '-')}`}>
      <div className="flex items-start justify-between gap-2 pb-2 border-b border-slate-100">
        <span className="font-bold text-slate-900">{label}</span>
        <span className="text-xs text-slate-500 text-right">{nb} demandé{nb > 1 ? 's' : ''}{aff > nb ? ` · ${aff} affiches` : ''}</span>
      </div>
      <div className={wide ? 'grid md:grid-cols-2 gap-x-8' : ''}>
        {emps.map((e, i) => (
          <div key={i} className="flex items-start justify-between gap-3 py-2 border-b border-slate-100 last:border-0 text-sm">
            <span className="text-slate-800">{e.nom || '—'}</span>
            <span className="font-bold text-slate-900 whitespace-nowrap">× {Number(e.affiches) || 1}</span>
          </div>
        ))}
      </div>
      {missing > 0 && (
        <div className="mt-2 rounded-lg bg-amber-50 text-amber-800 text-xs px-3 py-2">{missing} panneau{missing > 1 ? 'x' : ''} annoncé{missing > 1 ? 's' : ''} mais non détaillé{missing > 1 ? 's' : ''} dans le message</div>
      )}
    </div>
  );
}

function RequestCard({ request, onEffectuer, onProspect, onDelete, onMarkTreated }) {
  const [showMsg, setShowMsg] = useState(false);
  const [badgeCls, statutLabel] = STATUT_BADGE[request.statut] || STATUT_BADGE['Nouvelle'];
  const panneaux = Array.isArray(request.panneaux) ? request.panneaux.filter((p) => p && (p.type_panneau || (p.emplacements || []).length)) : [];
  const totalPanneaux = panneaux.reduce((x, p) => x + (Number(p.nb_panneaux) || (p.emplacements || []).length || 0), 0);
  const totalAffiches = panneaux.reduce((x, p) => x + (p.emplacements || []).reduce((y, e) => y + (Number(e.affiches) || 0), 0), 0);
  const fin = request.date_fin_campagne ? new Date(request.date_fin_campagne) : null;
  const debut = request.date_debut_campagne ? new Date(request.date_debut_campagne) : null;
  const overdue = fin && fin < new Date() && request.statut !== 'Devis_créé';
  const fmt = (d) => d.toLocaleDateString('fr-FR');
  const periode = debut && fin ? `${fmt(debut)} → ${fmt(fin)}` : fin ? `Jusqu'au ${fmt(fin)}` : debut ? `À partir du ${fmt(debut)}` : null;
  const summary = panneaux.length === 1
    ? `${totalPanneaux} ${(TYPE_STYLE[panneaux[0].type_panneau]?.[1] || 'panneaux').toLowerCase()}${totalAffiches > totalPanneaux ? ` · ${totalAffiches} affiches` : ''}`
    : `${totalPanneaux} panneaux · ${panneaux.length} types`;
  const sid = request.id.substring(0, 8);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm px-5 py-4 cs-rise" data-testid={`inbox-card-${sid}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-4 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-orange-500 text-white flex items-center justify-center font-extrabold text-sm flex-shrink-0">{initialsFromName(request.societe)}</div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-lg font-extrabold text-slate-900 uppercase truncate">{request.societe}</h3>
              <span className={`px-2 py-0.5 rounded-md font-bold text-xs ${badgeCls}`} data-testid={`inbox-statut-${sid}`}>{statutLabel}</span>
              {request.secteur && <span className="px-2 py-0.5 rounded-md font-bold text-xs bg-blue-50 text-blue-700">{request.secteur}</span>}
            </div>
            <div className="text-sm">
              <span className="text-slate-500">Reçue le {formatDateTime(request.received_at)} · <b className="text-slate-700">{timeAgo(request.received_at)}</b> · via {request.source || 'sesamrca.com'}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={onProspect} disabled={request.statut === 'Prospect_créé' || request.statut === 'Devis_créé'} className="h-9 px-3 font-bold" data-testid={`btn-ajouter-prospect-${sid}`}>Ajouter comme prospect</Button>
          <Button onClick={onEffectuer} disabled={request.statut === 'Devis_créé'} className="h-9 px-3 bg-green-600 hover:bg-green-700 text-white font-bold" data-testid={`btn-effectuer-devis-${sid}`}>Effectuer le devis</Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-9 w-9 p-0" data-testid={`btn-more-${sid}`}><MoreHorizontal className="h-4 w-4" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {request.statut !== 'Traitée' && request.statut !== 'Devis_créé' && (
                <DropdownMenuItem onClick={onMarkTreated} data-testid={`btn-mark-treated-${sid}`}><CheckCircle2 className="h-4 w-4 mr-2 text-emerald-600" /> Marquer comme traitée</DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={onDelete} className="text-rose-600" data-testid={`btn-supprimer-${sid}`}><Trash2 className="h-4 w-4 mr-2" /> Supprimer</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-[1fr_1fr_0.8fr_1fr_auto] gap-4 border-t border-slate-100 pt-3 mt-3 items-end">
        <div><div className="text-xs text-slate-500">Contact</div><div className="font-bold text-slate-900">{request.nom_complet || '—'}</div></div>
        <div>
          <div className="text-xs text-slate-500">Téléphone</div>
          <div className="font-bold text-slate-900">{request.telephone || '—'}</div>
          {request.telephone && (
            <div className="flex gap-3 text-sm font-bold text-blue-700">
              <a href={`tel:${request.telephone}`} className="hover:underline" data-testid={`inbox-call-${sid}`}>Appeler</a>
              <a href={waLink(request.telephone)} target="_blank" rel="noreferrer" className="hover:underline" data-testid={`inbox-wa-${sid}`}>WhatsApp</a>
            </div>
          )}
        </div>
        <div><div className="text-xs text-slate-500">Adresse</div><div className="font-bold text-slate-900 truncate">{request.adresse || '—'}</div></div>
        <div>
          <div className="text-xs text-slate-500">Période souhaitée</div>
          {periode ? (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-bold text-slate-900">{periode}</span>
              {overdue && <span className="px-2 py-0.5 rounded-md bg-red-50 text-red-600 text-sm font-bold" data-testid={`inbox-overdue-${sid}`}>Échéance dépassée</span>}
            </div>
          ) : <div className="text-slate-400">Non précisée</div>}
        </div>
        <button onClick={() => setShowMsg((v) => !v)} className="font-bold text-blue-700 hover:underline whitespace-nowrap text-sm" data-testid={`btn-message-${sid}`}>
          {showMsg ? 'Masquer le détail ‹' : "Voir le message d'origine ›"}
        </button>
      </div>

      {showMsg && (
        <div data-testid={`inbox-details-${sid}`}>
      <div className="flex items-center justify-between gap-3 mt-4 mb-3 flex-wrap">
        <div className="flex items-baseline gap-3">
          <span className="text-lg font-bold text-slate-900">{panneaux.length ? 'Demande de panneaux' : 'Demande'}</span>
          {panneaux.length > 0 && <span className="text-slate-500">{summary}</span>}
        </div>

      </div>
      {request.message && (
        <div className="mb-4 rounded-xl bg-amber-50/70 border-l-4 border-amber-300 p-4 text-sm text-slate-700 whitespace-pre-wrap" data-testid={`inbox-message-${sid}`}>{request.message}</div>
      )}
      {panneaux.length > 0 ? (
        <div className={`grid gap-4 ${panneaux.length === 1 ? 'grid-cols-1' : panneaux.length === 2 ? 'md:grid-cols-2' : panneaux.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2 xl:grid-cols-4'}`}>
          {panneaux.map((p, i) => <PanelTypeBox key={i} p={p} wide={panneaux.length === 1} />)}
        </div>
      ) : (
        <div className="flex gap-6 flex-wrap text-sm text-slate-700">
          {request.email && <span><span className="text-slate-500">Email : </span><a href={`mailto:${request.email}`} className="font-semibold hover:text-blue-600">{request.email}</a></span>}
          {request.budget && <span><span className="text-slate-500">Budget : </span><b>{request.budget}</b></span>}
          {request.duree && <span><span className="text-slate-500">Durée : </span><b>{request.duree}</b></span>}
          {!request.email && !request.budget && !request.duree && !request.message && <span className="text-slate-400">Aucun détail fourni.</span>}
        </div>
      )}
        </div>
      )}
    </div>
  );
}

function InfoCell({ label, value, link }) {
  const inner = (
    <div className={`font-bold text-slate-900 text-sm leading-tight ${link ? 'hover:text-blue-600' : ''}`}>
      {value || '—'}
    </div>
  );
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1">{label}</div>
      {link ? <a href={link}>{inner}</a> : inner}
    </div>
  );
}

function VisitRow({ visit, onDelete }) {
  const d = new Date(visit.visited_at);
  const dateStr = d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  const timeStr = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  const flag = flagEmoji(visit.country_code);
  const loc = [visit.city, visit.country].filter(Boolean).join(', ');

  return (
    <tr className="border-b border-slate-100 hover:bg-blue-50/40 transition" data-testid={`visit-row-${visit.id.substring(0, 8)}`}>
      <td className="py-3 px-4 whitespace-nowrap">
        <div className="font-semibold text-slate-800">{timeStr}</div>
        <div className="text-xs text-slate-400">{dateStr}</div>
      </td>
      <td className="py-3 px-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
            <User className="h-4 w-4 text-slate-500" />
          </div>
          <div className="min-w-0">
            {visit.visitor_name && (
              <div className="font-semibold text-slate-800 text-sm truncate max-w-[200px]" data-testid="visit-visitor-name">
                {visit.visitor_name}
                {visit.is_demo && <span className="ml-1.5 align-middle text-[9px] font-bold uppercase bg-amber-100 text-amber-700 px-1 py-0.5 rounded">démo</span>}
              </div>
            )}
            <div className="font-mono text-xs text-slate-700 truncate">{visit.ip || '—'}</div>
            <div className="text-[10px] text-slate-400 truncate max-w-[180px]" title={visit.user_agent}>
              {(visit.user_agent || '').split(')')[0].replace(/^Mozilla\/5\.0 \(/, '') || '—'}
            </div>
          </div>
        </div>
      </td>
      <td className="py-3 px-4">
        {loc ? (
          <div className="flex items-center gap-2">
            <span className="text-lg leading-none">{flag || '🌐'}</span>
            <div>
              <div className="font-semibold text-slate-800 text-xs">{loc}</div>
              {visit.isp && <div className="text-[10px] text-slate-400 truncate max-w-[160px]">{visit.isp}</div>}
            </div>
          </div>
        ) : <span className="text-slate-400 text-xs">—</span>}
      </td>
      <td className="py-3 px-4">
        <div className="font-mono text-xs text-slate-700 truncate max-w-[220px]" title={visit.page}>{visit.page}</div>
        {visit.page_title && <div className="text-[10px] text-slate-400 truncate max-w-[220px]">{visit.page_title}</div>}
      </td>
      <td className="py-3 px-4">
        {visit.referer ? (
          <a
            href={visit.referer}
            target="_blank" rel="noreferrer"
            className="text-xs text-blue-600 hover:underline truncate max-w-[160px] inline-block"
            title={visit.referer}
          >
            {new URL(visit.referer).hostname.replace('www.', '')}
          </a>
        ) : <span className="text-slate-400 text-xs">—</span>}
      </td>
      <td className="py-3 px-4 text-right">
        <button
          onClick={onDelete}
          className="p-1.5 rounded-md text-rose-500 hover:bg-rose-50 hover:text-rose-700"
          title="Supprimer"
          data-testid={`btn-delete-visit-${visit.id.substring(0, 8)}`}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </td>
    </tr>
  );
}

function InfoRow({ icon: Icon, label, value, link }) {
  const content = (
    <div className="flex items-start gap-2">
      <Icon className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" />
      <div className="min-w-0">
        <div className="text-xs text-slate-500">{label}</div>
        <div className={`font-semibold text-slate-800 truncate ${link ? 'hover:text-blue-600' : ''}`}>{value}</div>
      </div>
    </div>
  );
  return link ? <a href={link} className="hover:opacity-80">{content}</a> : content;
}

// ═══════════ SNIPPETS ═══════════

function CopyBlock({ snippet, note }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true); toast.success('Snippet copié !');
      setTimeout(() => setCopied(false), 2500);
    } catch { toast.error('Impossible de copier — sélectionnez et copiez manuellement'); }
  };
  return (
    <div className="space-y-4">
      {note}
      <div className="relative">
        <pre className="bg-slate-900 text-slate-100 rounded-lg p-4 text-xs overflow-x-auto max-h-[420px] overflow-y-auto">
          <code>{snippet}</code>
        </pre>
        <Button size="sm" onClick={copy} className="absolute top-2 right-2 bg-blue-600 hover:bg-blue-700" data-testid="copy-snippet">
          {copied ? '✓ Copié' : 'Copier'}
        </Button>
      </div>
    </div>
  );
}

function DevisSnippet({ endpoint }) {
  const snippet = `<!-- Ajoutez ce code AVANT </body> sur sesamrca.com -->
<script>
(function() {
  const CRM_ENDPOINT = "${endpoint}";
  document.addEventListener("click", async function(e) {
    const btn = e.target.closest('[data-devis-submit], button.demander, .btn-demander');
    if (!btn) return;
    const get = (name) => {
      const el = document.querySelector('[name="' + name + '"]') ||
                 document.querySelector('#' + name);
      return el ? (el.value || '').trim() : '';
    };
    const payload = {
      societe:      get('societe')     || get('company')  || get('nom_societe'),
      secteur:      get('secteur')     || get('activity'),
      adresse:      get('adresse')     || get('address'),
      nom_complet:  get('nom_complet') || get('name')     || get('fullname'),
      telephone:    get('telephone')   || get('phone')    || get('tel'),
      email:        get('email'),
      message:      get('message')     || get('details'),
      budget:       get('budget'),
      duree:        get('duree')       || get('duration'),
      source:       'sesamrca.com'
    };
    if (!payload.societe || !payload.nom_complet || !payload.telephone) return;
    try {
      await fetch(CRM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true
      });
    } catch (err) { console.warn('CRM notify failed', err); }
  }, true);
})();
</script>`;
  return (
    <CopyBlock
      snippet={snippet}
      note={
        <div className="bg-blue-50 rounded-lg p-3 text-sm text-blue-900 border border-blue-200">
          <p className="font-semibold mb-1">Instructions</p>
          <ol className="list-decimal ml-5 space-y-1 text-xs">
            <li>Ouvrez le code source de <code>www.sesamrca.com</code></li>
            <li>Collez le snippet ci-dessous juste avant <code>&lt;/body&gt;</code></li>
            <li>Adaptez le sélecteur du bouton "Demander" si nécessaire</li>
          </ol>
        </div>
      }
    />
  );
}

function VisitSnippet({ endpoint }) {
  const snippet = `<!-- Ajoutez ce code AVANT </body> sur sesamrca.com -->
<script>
(function() {
  const CRM_ENDPOINT = "${endpoint}";
  // Session ID stable par navigateur (localStorage)
  let sid = localStorage.getItem('sesam_sid');
  if (!sid) {
    sid = 'sid-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10);
    localStorage.setItem('sesam_sid', sid);
  }
  function sendVisit() {
    try {
      fetch(CRM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          page:       location.pathname + location.search,
          page_title: document.title,
          referer:    document.referrer || '',
          session_id: sid
        }),
        keepalive: true
      });
    } catch (e) {}
  }
  // Envoyer dès le chargement
  if (document.readyState === 'complete') sendVisit();
  else window.addEventListener('load', sendVisit);
  // Suivre les changements d'URL (SPA)
  let last = location.href;
  setInterval(function() {
    if (location.href !== last) { last = location.href; sendVisit(); }
  }, 1500);
})();
</script>`;
  return (
    <CopyBlock
      snippet={snippet}
      note={
        <div className="bg-sky-50 rounded-lg p-3 text-sm text-sky-900 border border-sky-200">
          <p className="font-semibold mb-1">Instructions</p>
          <ol className="list-decimal ml-5 space-y-1 text-xs">
            <li>Collez ce snippet avant <code>&lt;/body&gt;</code> sur toutes les pages de sesamrca.com</li>
            <li>Chaque visite remontera dans l'onglet <strong>Visites</strong> avec date, heure, ville et pays approximatifs</li>
            <li>Les visiteurs d'une même session (même navigateur) partagent un <code>session_id</code> stable</li>
          </ol>
        </div>
      }
    />
  );
}
